import React, { useState, Fragment } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { ShieldCheckIcon, SearchIcon, CheckIcon, XIcon } from 'lucide-react';
// Types for Permission Matrix
type PermissionType = 'view' | 'createEdit' | 'delete' | 'approval' | 'forward';
interface ModulePermission {
  id: string;
  name: string;
  permissions: Record<PermissionType, boolean>;
  subModules?: ModulePermission[];
}
export function RolePermission() {
  const [activeTab, setActiveTab] = useState<'list' | 'create' | 'assign' | 'audit'>('list');
  const [currentStep, setCurrentStep] = useState(0);
  // Mock Data: Roles
  const roles = [{
    id: 'ROLE-001',
    name: 'HQ Admin',
    description: 'Full system access across all stations',
    usersCount: 5,
    createdDate: '2023-01-15',
    status: 'active' as const
  }, {
    id: 'ROLE-002',
    name: 'Range Officer',
    description: 'Manage operations within a specific range',
    usersCount: 12,
    createdDate: '2023-02-20',
    status: 'active' as const
  }, {
    id: 'ROLE-003',
    name: 'Station Officer',
    description: 'Manage operations at a specific station',
    usersCount: 45,
    createdDate: '2023-03-10',
    status: 'active' as const
  }, {
    id: 'ROLE-004',
    name: 'Field Officer',
    description: 'Mobile app access for field verification',
    usersCount: 120,
    createdDate: '2023-04-05',
    status: 'active' as const
  }, {
    id: 'ROLE-005',
    name: 'Approver',
    description: 'Can only approve/reject applications',
    usersCount: 8,
    createdDate: '2023-05-12',
    status: 'active' as const
  }, {
    id: 'ROLE-006',
    name: 'Data Entry Operator',
    description: 'Can only create records, no approval rights',
    usersCount: 25,
    createdDate: '2023-06-18',
    status: 'inactive' as const
  }];
  // Mock Data: Users for Assignment
  const usersForAssignment = [{
    id: 'USR-001',
    name: 'Rahim Uddin',
    station: 'Chandpai Range',
    roles: ['Range Officer', 'Approver'],
    lastUpdated: '2024-03-25'
  }, {
    id: 'USR-002',
    name: 'Karim Sheikh',
    station: 'Katka Station',
    roles: ['Station Officer'],
    lastUpdated: '2024-03-20'
  }, {
    id: 'USR-003',
    name: 'Fatema Begum',
    station: 'Dhangmari Station',
    roles: ['Field Officer'],
    lastUpdated: '2024-03-15'
  }, {
    id: 'USR-004',
    name: 'Abdul Hakim',
    station: 'Headquarters',
    roles: ['HQ Admin'],
    lastUpdated: '2024-03-10'
  }, {
    id: 'USR-005',
    name: 'Jamal Hossain',
    station: 'Harbaria Station',
    roles: ['Data Entry Operator'],
    lastUpdated: '2024-03-05'
  }];
  // Mock Data: Audit Logs
  const auditLogs = [{
    id: 'AUD-001',
    timestamp: '2024-03-30 14:30:22',
    user: 'Admin User',
    action: 'Updated Permission',
    target: 'Role: Station Officer',
    details: 'Added Delete permission for Beneficiary Registry',
    ip: '192.168.1.45'
  }, {
    id: 'AUD-002',
    timestamp: '2024-03-29 10:15:00',
    user: 'Admin User',
    action: 'Assigned Role',
    target: 'User: Rahim Uddin',
    details: 'Assigned Approver role',
    ip: '192.168.1.45'
  }, {
    id: 'AUD-003',
    timestamp: '2024-03-28 16:45:10',
    user: 'System',
    action: 'Created Role',
    target: 'Role: Data Entry Operator',
    details: 'New role created with limited permissions',
    ip: '127.0.0.1'
  }, {
    id: 'AUD-004',
    timestamp: '2024-03-25 09:20:05',
    user: 'Admin User',
    action: 'Removed Role',
    target: 'User: Jamal Hossain',
    details: 'Removed Field Officer role',
    ip: '192.168.1.45'
  }, {
    id: 'AUD-005',
    timestamp: '2024-03-20 11:10:30',
    user: 'Admin User',
    action: 'Updated Permission',
    target: 'Role: Field Officer',
    details: 'Removed Create/Edit permission for Permits',
    ip: '192.168.1.45'
  }, {
    id: 'AUD-006',
    timestamp: '2024-03-15 15:55:45',
    user: 'Admin User',
    action: 'Assigned Role',
    target: 'User: Fatema Begum',
    details: 'Assigned Field Officer role',
    ip: '192.168.1.45'
  }, {
    id: 'AUD-007',
    timestamp: '2024-03-10 13:40:12',
    user: 'System',
    action: 'Created Role',
    target: 'Role: Approver',
    details: 'New role created for approval workflows',
    ip: '127.0.0.1'
  }, {
    id: 'AUD-008',
    timestamp: '2024-03-05 08:30:00',
    user: 'Admin User',
    action: 'Updated Permission',
    target: 'Role: HQ Admin',
    details: 'Granted all permissions across all modules',
    ip: '192.168.1.45'
  }];
  // Data Table Columns
  const roleColumns = [{
    key: 'name',
    label: 'Role Name'
  }, {
    key: 'description',
    label: 'Description'
  }, {
    key: 'usersCount',
    label: 'Users Count'
  }, {
    key: 'createdDate',
    label: 'Created Date'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-2259" data-mp="1r998w3milkdu" />
  }];
  const assignColumns = [{
    key: 'name',
    label: 'User Name'
  }, {
    key: 'roles',
    label: 'Assigned Roles',
    render: (roles: string[]) => <div className="flex flex-wrap gap-1" data-id="element-2260" data-mp="2e7vwlqfaaszw">
          {roles.map((role, idx) => <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2261" data-mp="2e5jbj4e8c2im" data-mpi={idx}>
              {role}
            </span>)}
        </div>
  }, {
    key: 'station',
    label: 'Station/Range'
  }, {
    key: 'lastUpdated',
    label: 'Last Updated'
  }];
  const auditColumns = [{
    key: 'timestamp',
    label: 'Timestamp'
  }, {
    key: 'user',
    label: 'User'
  }, {
    key: 'action',
    label: 'Action',
    render: (value: string) => {
      let color = 'bg-gray-100 text-gray-800';
      if (value.includes('Created')) color = 'bg-green-100 text-green-800';
      if (value.includes('Updated')) color = 'bg-blue-100 text-blue-800';
      if (value.includes('Assigned')) color = 'bg-purple-100 text-purple-800';
      if (value.includes('Removed')) color = 'bg-red-100 text-red-800';
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`} data-id="element-2262" data-mp="7dd5f9i99z8g">
            {value}
          </span>;
    }
  }, {
    key: 'target',
    label: 'Target'
  }, {
    key: 'details',
    label: 'Details'
  }, {
    key: 'ip',
    label: 'IP Address'
  }];
  // Filters
  const roleFilters = [{
    key: 'status',
    label: 'Status',
    options: ['Active', 'Inactive']
  }];
  const auditFilters = [{
    key: 'action',
    label: 'Action Type',
    options: ['Created Role', 'Updated Permission', 'Assigned Role', 'Removed Role']
  }];
  // Wizard Steps
  const wizardSteps = [{
    title: 'Role Info',
    description: 'Basic details of the role'
  }, {
    title: 'Permission Matrix',
    description: 'Configure module and feature access'
  }, {
    title: 'Review & Save',
    description: 'Review permissions before saving'
  }];
  // Permission Matrix State
  const initialPermissions: ModulePermission[] = [{
    id: 'dashboard',
    name: 'Dashboard',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    }
  }, {
    id: 'user_mgmt',
    name: 'User Management',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    },
    subModules: [{
      id: 'user_list',
      name: 'User List',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'role_perm',
      name: 'Role & Permission',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }]
  }, {
    id: 'beneficiary',
    name: 'Beneficiary Registry',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    },
    subModules: [{
      id: 'ben_list',
      name: 'Beneficiary List',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'ben_add',
      name: 'Add Beneficiary',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'ben_approve',
      name: 'Approval Queue',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }]
  }, {
    id: 'permit',
    name: 'Permit Management',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    },
    subModules: [{
      id: 'perm_apps',
      name: 'Applications',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'perm_apply',
      name: 'Apply Permit',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'perm_track',
      name: 'Trip Tracking',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'perm_hist',
      name: 'Trip History',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }]
  }, {
    id: 'vessel',
    name: 'Vessel Management',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    }
  }, {
    id: 'compliance',
    name: 'Forest Compliance',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    },
    subModules: [{
      id: 'comp_cases',
      name: 'Case Registry',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'comp_blacklist',
      name: 'Blacklist',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }]
  }, {
    id: 'smartid',
    name: 'Smart ID & RFID',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    }
  }, {
    id: 'reports',
    name: 'Reports & Analytics',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    }
  }];
  const [permissions, setPermissions] = useState<ModulePermission[]>(initialPermissions);
  const [roleInfo, setRoleInfo] = useState({
    name: '',
    description: '',
    status: 'active'
  });
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({
    user_mgmt: true,
    beneficiary: true,
    permit: true,
    compliance: true
  });
  // Toggle module expansion
  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };
  // Handle individual checkbox change
  const handlePermissionChange = (moduleId: string, subModuleId: string | null, permType: PermissionType, value: boolean) => {
    setPermissions(prev => {
      const newPerms = [...prev];
      const modIndex = newPerms.findIndex(m => m.id === moduleId);
      if (modIndex === -1) return prev;
      if (subModuleId) {
        // Update sub-module
        const subIndex = newPerms[modIndex].subModules?.findIndex(s => s.id === subModuleId) ?? -1;
        if (subIndex !== -1 && newPerms[modIndex].subModules) {
          newPerms[modIndex].subModules![subIndex].permissions[permType] = value;
          // If a sub-module gets a permission, ensure the parent module has at least 'view'
          if (value && permType !== 'view') {
            newPerms[modIndex].permissions['view'] = true;
          }
        }
      } else {
        // Update main module
        newPerms[modIndex].permissions[permType] = value;
        // If parent gets a permission, apply to all sub-modules (optional behavior, implementing for ease of use)
        if (newPerms[modIndex].subModules) {
          newPerms[modIndex].subModules!.forEach(sub => {
            sub.permissions[permType] = value;
          });
        }
      }
      return newPerms;
    });
  };
  // Handle row 'Select All'
  const handleRowSelectAll = (moduleId: string, subModuleId: string | null, value: boolean) => {
    const permTypes: PermissionType[] = ['view', 'createEdit', 'delete', 'approval', 'forward'];
    permTypes.forEach(pt => handlePermissionChange(moduleId, subModuleId, pt, value));
  };
  // Handle column 'Select All'
  const handleColumnSelectAll = (permType: PermissionType, value: boolean) => {
    permissions.forEach(mod => {
      handlePermissionChange(mod.id, null, permType, value);
    });
  };
  // Assignment State
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const handleUserSelect = (userId: string) => {
    setSelectedUser(userId);
    const user = usersForAssignment.find(u => u.id === userId);
    if (user) {
      setSelectedRoles(user.roles);
    }
  };
  const toggleRoleAssignment = (roleName: string) => {
    setSelectedRoles(prev => prev.includes(roleName) ? prev.filter(r => r !== roleName) : [...prev, roleName]);
  };
  return <div className="space-y-6" data-id="element-2263" data-mp="1hrd6ijg7oiwe">
      <div data-id="element-2264" data-mp="1hs63wtta5w1v">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2265" data-mp="1hselgz0clpje">
          Role & Permission Management
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2266" data-mp="1mqg119ykf6q4">
          Configure access control and assign roles to users
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-2267" data-mp="1csjr2lhd6htl">
        <div className="flex gap-4" data-id="element-2268" data-mp="1cr8mijg3h61f">
          <button onClick={() => setActiveTab('list')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'list' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2269" data-mp="tn595fd0b39u">
            Role List
          </button>
          <button onClick={() => setActiveTab('create')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'create' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2270" data-mp="ezag1xv36ww8">
            Create/Edit Role
          </button>
          <button onClick={() => setActiveTab('assign')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'assign' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2271" data-mp="12m8rui89hgnt">
            Assign Roles
          </button>
          <button onClick={() => setActiveTab('audit')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'audit' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2272" data-mp="1xx6zs9k6n029">
            Audit Log
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'list' && <div className="space-y-4" data-id="element-2273" data-mp="2hlg23cffwmfh">
          <div className="flex justify-end" data-id="element-2274" data-mp="2hk4pcn40o4c8">
            <button onClick={() => setActiveTab('create')} className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] transition-colors" data-id="element-2275" data-mp="2hnintk9k7tee">
              + Add New Role
            </button>
          </div>
          <DataTable columns={roleColumns} data={roles} filters={roleFilters} onEdit={row => {
        setRoleInfo({
          name: row.name,
          description: row.description,
          status: row.status
        });
        setActiveTab('create');
        setCurrentStep(0);
      }} searchPlaceholder="Search roles..." data-id="element-2276" data-mp="2vjmifeoci3re" />
        </div>}

      {activeTab === 'create' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      alert('Role saved successfully!');
      setCurrentStep(0);
      setActiveTab('list');
      setRoleInfo({
        name: '',
        description: '',
        status: 'active'
      });
      setPermissions(initialPermissions);
    }} data-id="element-2277" data-mp="3ctqdaa29wy0u">
          {currentStep === 0 && <div className="space-y-4 max-w-2xl" data-id="element-2278" data-mp="2p3458is9rdqu">
              <div data-id="element-2279" data-mp="9dax4crndxhe">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2280" data-mp="9cro5ljz6jfi">
                  Role Name *
                </label>
                <input type="text" value={roleInfo.name} onChange={e => setRoleInfo({
            ...roleInfo,
            name: e.target.value
          })} placeholder="e.g., Senior Approver" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2281" data-mp="4e6obn8my1rl" />
              </div>
              <div data-id="element-2282" data-mp="uqjtb7dx6ndw">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2283" data-mp="znjyzh6uxo4h">
                  Description
                </label>
                <textarea rows={3} value={roleInfo.description} onChange={e => setRoleInfo({
            ...roleInfo,
            description: e.target.value
          })} placeholder="Describe the purpose and access level of this role..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2284" data-mp="lv3su7int9zc"></textarea>
              </div>
              <div data-id="element-2285" data-mp="309c98oucnjkm">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2286" data-mp="309vq0289k1x2">
                  Status
                </label>
                <div className="flex gap-4" data-id="element-2287" data-mp="3574ai9gvwi1h">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-2288" data-mp="2r8nvzwcs5n8s">
                    <input type="radio" name="status" value="active" checked={roleInfo.status === 'active'} onChange={() => setRoleInfo({
                ...roleInfo,
                status: 'active'
              })} className="text-[#1B5E20] focus:ring-[#1B5E20]" data-id="element-2289" data-mp="2raqxbcjdv2st" />
                    <span className="text-sm" data-id="element-2290" data-mp="kg3um4e4e2ld">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-2291" data-mp="250saea9y1cwq">
                    <input type="radio" name="status" value="inactive" checked={roleInfo.status === 'inactive'} onChange={() => setRoleInfo({
                ...roleInfo,
                status: 'inactive'
              })} className="text-[#1B5E20] focus:ring-[#1B5E20]" data-id="element-2292" data-mp="20279e8mvap78" />
                    <span className="text-sm" data-id="element-2293" data-mp="2xnld2y6w8m9d">Inactive</span>
                  </label>
                </div>
              </div>
            </div>}

          {currentStep === 1 && <div className="space-y-4" data-id="element-2294" data-mp="13by5eb2wkrbk">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 mb-4" data-id="element-2295" data-mp="13czl4suv61zm">
                Configure permissions for{' '}
                <strong data-id="element-2296" data-mp="2yf38jeqdz87c">{roleInfo.name || 'New Role'}</strong>. Checking a
                parent module's permission will automatically apply it to all
                its sub-modules.
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2297" data-mp="37dygav92wqcr">
                <div className="overflow-x-auto" data-id="element-2298" data-mp="3ce2x6mdwfn3i">
                  <table className="w-full text-sm text-left" data-id="element-2299" data-mp="3cbq7to5taagf">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-700" data-id="element-2300" data-mp="3hc4gn8f8lofi">
                      <tr data-id="element-2301" data-mp="3mayxw5xk34xi">
                        <th className="px-4 py-3 font-semibold w-1/3" data-id="element-2302" data-mp="3ma603ugkrpri">
                          Module / Feature
                        </th>
                        <th className="px-4 py-3 font-semibold text-center" data-id="element-2303" data-mp="3v42iqzaj6k26">
                          <div data-id="element-2304" data-mp="3v6561bkatz37">View</div>
                          <input type="checkbox" className="mt-1 rounded text-[#1B5E20]" onChange={e => handleColumnSelectAll('view', e.target.checked)} data-id="element-2305" data-mp="3ysqf0z2dtpe" />
                        </th>
                        <th className="px-4 py-3 font-semibold text-center" data-id="element-2306" data-mp="e4as8tv3lhpp">
                          <div data-id="element-2307" data-mp="934xkqhvnkdj">Create/Edit</div>
                          <input type="checkbox" className="mt-1 rounded text-[#1B5E20]" onChange={e => handleColumnSelectAll('createEdit', e.target.checked)} data-id="element-2308" data-mp="51rq6bz4z94i" />
                        </th>
                        <th className="px-4 py-3 font-semibold text-center" data-id="element-2309" data-mp="nzvpyft4vvtn">
                          <div data-id="element-2310" data-mp="1kitk5gr7yhdt">Delete</div>
                          <input type="checkbox" className="mt-1 rounded text-[#1B5E20]" onChange={e => handleColumnSelectAll('delete', e.target.checked)} data-id="element-2311" data-mp="1frbjktt3yitd" />
                        </th>
                        <th className="px-4 py-3 font-semibold text-center" data-id="element-2312" data-mp="r9x4ymlloqk5">
                          <div data-id="element-2313" data-mp="v9fiuyf081dh">Approval</div>
                          <input type="checkbox" className="mt-1 rounded text-[#1B5E20]" onChange={e => handleColumnSelectAll('approval', e.target.checked)} data-id="element-2314" data-mp="vca0yjajpa3q" />
                        </th>
                        <th className="px-4 py-3 font-semibold text-center" data-id="element-2315" data-mp="21ye1mfvkj3l6">
                          <div data-id="element-2316" data-mp="26p46ls0314fc">Forward</div>
                          <input type="checkbox" className="mt-1 rounded text-[#1B5E20]" onChange={e => handleColumnSelectAll('forward', e.target.checked)} data-id="element-2317" data-mp="26qxch52vfkma" />
                        </th>
                        <th className="px-4 py-3 font-semibold text-center border-l border-gray-200" data-id="element-2318" data-mp="k8qdah9feb">
                          <div data-id="element-2319" data-mp="38gdqevgjowwy">All</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200" data-id="element-2320" data-mp="33ivpnscolo01">
                      {permissions.map(module => <Fragment key={module.id} data-id="element-2321" data-mp="3igo0i77a9o7w">
                          {/* Main Module Row */}
                          <tr className="bg-white hover:bg-gray-50" data-id="element-2322" data-mp="3deoyb0na4xcj">
                            <td className="px-4 py-3 font-medium flex items-center gap-2" data-id="element-2323" data-mp="xnt0ur4zhmpa">
                              {module.subModules && <button onClick={() => toggleModule(module.id)} className="text-gray-500 hover:text-gray-700 w-4" data-id="element-2324" data-mp="ss3wmai0rhmn">
                                  {expandedModules[module.id] ? '▼' : '▶'}
                                </button>}
                              {!module.subModules && <span className="w-4" data-id="element-2325" data-mp="h2zrccn3i6hh"></span>}
                              {module.name}
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-2326" data-mp="lz86vr57g1p6">
                              <input type="checkbox" checked={module.permissions.view} onChange={e => handlePermissionChange(module.id, null, 'view', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-4 w-4" data-id="element-2327" data-mp="qz2xdtl2ip9p" />
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-2328" data-mp="22kie19cvgys9">
                              <input type="checkbox" checked={module.permissions.createEdit} onChange={e => handlePermissionChange(module.id, null, 'createEdit', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-4 w-4" data-id="element-2329" data-mp="1xm8hovfevs52" />
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-2330" data-mp="32sov93v91vph">
                              <input type="checkbox" checked={module.permissions.delete} onChange={e => handlePermissionChange(module.id, null, 'delete', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-4 w-4" data-id="element-2331" data-mp="32s5evrl8zfdk" />
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-2332" data-mp="1w59x1p7djqpd">
                              <input type="checkbox" checked={module.permissions.approval} onChange={e => handlePermissionChange(module.id, null, 'approval', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-4 w-4" data-id="element-2333" data-mp="2131tmwiys0zw" />
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-2334" data-mp="2a67a3l4bvshz">
                              <input type="checkbox" checked={module.permissions.forward} onChange={e => handlePermissionChange(module.id, null, 'forward', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-4 w-4" data-id="element-2335" data-mp="2a5feiaaqdurp" />
                            </td>
                            <td className="px-4 py-3 text-center border-l border-gray-200 bg-gray-50" data-id="element-2336" data-mp="32jfmigi7nrj4">
                              <input type="checkbox" checked={Object.values(module.permissions).every(Boolean)} onChange={e => handleRowSelectAll(module.id, null, e.target.checked)} className="rounded text-gray-600 focus:ring-gray-500 h-4 w-4" data-id="element-2337" data-mp="2xlw7hedovaq4" />
                            </td>
                          </tr>

                          {/* Sub Modules Rows */}
                          {module.subModules && expandedModules[module.id] && module.subModules.map(sub => <tr key={sub.id} className="bg-gray-50/50 hover:bg-gray-100/50" data-id="element-2338" data-mp="6nsws59kn4rk">
                                <td className="px-4 py-2 pl-12 text-gray-600" data-id="element-2339" data-mp="fn69r7wwmbh1">
                                  ↳ {sub.name}
                                </td>
                                <td className="px-4 py-2 text-center" data-id="element-2340" data-mp="kgiwtdlxqa7o">
                                  <input type="checkbox" checked={sub.permissions.view} onChange={e => handlePermissionChange(module.id, sub.id, 'view', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-3.5 w-3.5" data-id="element-2341" data-mp="3k1ehachtnst6" />
                                </td>
                                <td className="px-4 py-2 text-center" data-id="element-2342" data-mp="7deyyh73q4qy">
                                  <input type="checkbox" checked={sub.permissions.createEdit} onChange={e => handlePermissionChange(module.id, sub.id, 'createEdit', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-3.5 w-3.5" data-id="element-2343" data-mp="lcejbcnkzxhb" />
                                </td>
                                <td className="px-4 py-2 text-center" data-id="element-2344" data-mp="2iyp9085e4584">
                                  <input type="checkbox" checked={sub.permissions.delete} onChange={e => handlePermissionChange(module.id, sub.id, 'delete', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-3.5 w-3.5" data-id="element-2345" data-mp="2j0idbgsj6dcy" />
                                </td>
                                <td className="px-4 py-2 text-center" data-id="element-2346" data-mp="190z6707x10cc">
                                  <input type="checkbox" checked={sub.permissions.approval} onChange={e => handlePermissionChange(module.id, sub.id, 'approval', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-3.5 w-3.5" data-id="element-2347" data-mp="142fiau9rhehc" />
                                </td>
                                <td className="px-4 py-2 text-center" data-id="element-2348" data-mp="2nzmzafx9plr1">
                                  <input type="checkbox" checked={sub.permissions.forward} onChange={e => handlePermissionChange(module.id, sub.id, 'forward', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-3.5 w-3.5" data-id="element-2349" data-mp="2rzx0lfa47nzk" />
                                </td>
                                <td className="px-4 py-2 text-center border-l border-gray-200" data-id="element-2350" data-mp="3pp8g05ee3abk">
                                  <input type="checkbox" checked={Object.values(sub.permissions).every(Boolean)} onChange={e => handleRowSelectAll(module.id, sub.id, e.target.checked)} className="rounded text-gray-500 focus:ring-gray-500 h-3.5 w-3.5" data-id="element-2351" data-mp="3untm2lgboo7x" />
                                </td>
                              </tr>)}
                        </Fragment>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>}

          {currentStep === 2 && <div className="space-y-6" data-id="element-2352" data-mp="dsnia802nox0">
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-2353" data-mp="dtq5o5y6r986">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2354" data-mp="iqglqt9zq4tl">
                  Role Summary
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-2355" data-mp="422l8p01qsg7">
                  <div data-id="element-2356" data-mp="915d17r0n268">
                    <div className="text-sm text-gray-500" data-id="element-2357" data-mp="90dv2zqcd2yf">Role Name</div>
                    <div className="font-medium text-gray-900" data-id="element-2358" data-mp="3r6s4qmhkuqr0">
                      {roleInfo.name || 'Not specified'}
                    </div>
                  </div>
                  <div data-id="element-2359" data-mp="3v6kaysji0kt7">
                    <div className="text-sm text-gray-500" data-id="element-2360" data-mp="3h9nhskfwqr95">Status</div>
                    <div data-id="element-2361" data-mp="3hbgry9pqbtlw">
                      <StatusBadge status={roleInfo.status as any} data-id="element-2362" data-mp="3m7p2f9szeymq" />
                    </div>
                  </div>
                  <div className="col-span-2" data-id="element-2363" data-mp="1fiskfo61yk0b">
                    <div className="text-sm text-gray-500" data-id="element-2364" data-mp="1ffy15zyrz58h">Description</div>
                    <div className="text-gray-900" data-id="element-2365" data-mp="1kgc0mtmb96uw">
                      {roleInfo.description || 'No description provided.'}
                    </div>
                  </div>
                </div>

                <h4 className="font-semibold text-gray-900 mb-3 border-t border-gray-200 pt-4" data-id="element-2366" data-mp="38xeh7w7e78s">
                  Granted Permissions Overview
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm" data-id="element-2367" data-mp="3porzdc60d8a2">
                  {permissions.map(mod => {
              const activePerms = Object.entries(mod.permissions).filter(([_, val]) => val).map(([key]) => key.replace('createEdit', 'Create/Edit').replace(/^\w/, c => c.toUpperCase()));
              if (activePerms.length === 0) return null;
              return <div key={mod.id} className="flex justify-between items-start py-1 border-b border-gray-100 last:border-0" data-id="element-2368" data-mp="14xrestsau4nm">
                        <span className="font-medium text-gray-700" data-id="element-2369" data-mp="1hg3m7nidb9b5">
                          {mod.name}
                        </span>
                        <span className="text-gray-500 text-right" data-id="element-2370" data-mp="1m84xb63sutzl">
                          {activePerms.join(', ')}
                        </span>
                      </div>;
            })}
                </div>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'assign' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2371" data-mp="13glrysmaz3ue">
          {/* Left Column: User Selection */}
          <div className="lg:col-span-1 bg-white rounded-lg border border-gray-200 p-4 flex flex-col h-[600px]" data-id="element-2372" data-mp="17hxlvac7mb8e">
            <h3 className="font-semibold text-gray-900 mb-4" data-id="element-2373" data-mp="2ewaghof7gaxb">1. Select User</h3>
            <div className="relative mb-4" data-id="element-2374" data-mp="2ixck0hg5n953">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-2375" data-mp="2iw1by5foff86" />
              <input type="text" placeholder="Search users..." className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20] text-sm" data-id="element-2376" data-mp="2jsc7ttbkotw0" />
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 pr-2" data-id="element-2377" data-mp="2sqxotk1ec0uo">
              {usersForAssignment.map(user => <button key={user.id} onClick={() => handleUserSelect(user.id)} className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedUser === user.id ? 'border-[#1B5E20] bg-green-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2378" data-mp="32r6ox8yie6fc">
                  <div className="font-medium text-gray-900" data-id="element-2379" data-mp="3gk61b22zvkio">{user.name}</div>
                  <div className="text-xs text-gray-500 mb-2" data-id="element-2380" data-mp="3lh6ctsim5fh6">
                    {user.station}
                  </div>
                  <div className="flex flex-wrap gap-1" data-id="element-2381" data-mp="19vdyqirf7whj">
                    {user.roles.map((role, idx) => <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600" data-id="element-2382" data-mp="14u6lw5af8740" data-mpi={idx}>
                        {role}
                      </span>)}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Right Column: Role Assignment */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6 flex flex-col h-[600px]" data-id="element-2383" data-mp="1jsihe8m259tl">
            <h3 className="font-semibold text-gray-900 mb-1" data-id="element-2384" data-mp="1euh09t86zkkr">
              2. Assign Roles
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-2385" data-mp="2ca4n57d09g75">
              {selectedUser ? `Select roles for ${usersForAssignment.find(u => u.id === selectedUser)?.name}` : 'Please select a user from the list first'}
            </p>

            <div className={`flex-1 overflow-y-auto pr-2 space-y-3 ${!selectedUser ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-2386" data-mp="2vdokj0a074nc">
              {roles.filter(r => r.status === 'active').map(role => {
            const isSelected = selectedRoles.includes(role.name);
            return <label key={role.id} className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'border-[#1B5E20] bg-green-50/30' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-2387" data-mp="27msofqyilnaa">
                      <div className="mt-0.5" data-id="element-2388" data-mp="22pj0kfqrxjok">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleRoleAssignment(role.name)} className="w-4 h-4 text-[#1B5E20] rounded focus:ring-[#1B5E20]" data-id="element-2389" data-mp="3t1q1v17fdisp" />
                      </div>
                      <div className="flex-1" data-id="element-2390" data-mp="1hfeet08k8lr6">
                        <div className="font-medium text-gray-900" data-id="element-2391" data-mp="1v5hv7akrdl8p">
                          {role.name}
                        </div>
                        <div className="text-sm text-gray-500 mt-1" data-id="element-2392" data-mp="1qeja7xotryz4">
                          {role.description}
                        </div>
                      </div>
                    </label>;
          })}
            </div>

            <div className="pt-4 border-t border-gray-200 mt-4 flex justify-end" data-id="element-2393" data-mp="206p6hjm412k4">
              <button disabled={!selectedUser} onClick={() => {
            alert('Roles assigned successfully!');
            setSelectedUser(null);
            setSelectedRoles([]);
          }} className="px-6 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-2394" data-mp="2e3kpo4w2sadw">
                Save Assignments
              </button>
            </div>
          </div>

          {/* Bottom: Current Assignments Table */}
          <div className="lg:col-span-3 mt-6" data-id="element-2395" data-mp="q6bgd0oq4m1l">
            <h3 className="font-semibold text-gray-900 mb-4" data-id="element-2396" data-mp="q4i8xj6adxsj">
              Current Assignments Overview
            </h3>
            <DataTable columns={assignColumns} data={usersForAssignment} onEdit={row => handleUserSelect(row.id)} searchPlaceholder="Search users..." data-id="element-2397" data-mp="3tnlcvko0uvsx" />
          </div>
        </div>}

      {activeTab === 'audit' && <div className="space-y-4" data-id="element-2398" data-mp="c6tqsj3lt4r6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900" data-id="element-2399" data-mp="c7vcrim5rbno">
            Audit logs track all changes made to roles, permissions, and user
            assignments for security and compliance purposes.
          </div>
          <DataTable columns={auditColumns} data={auditLogs} filters={auditFilters} searchPlaceholder="Search logs..." data-id="element-2400" data-mp="3ftkdqpt5r6s1" />
        </div>}
    </div>;
}